import { User } from '.';
import { unformat } from 'accounting';
import { action, computed, observable, reaction } from 'mobx';
import { app } from './../App';
import {
  IPricingRequestDetails,
  IPricingRequest,
  IGetPricingRequestApi,
  StatusOfNewPricingRequest,
} from 'common/interfaces/pricing';

export class PricingRequest {
  constructor(private user: User) {}

  // React to user change
  private reactToUserChange = reaction(
    () => this.user.id,
    (id: number) => {
      if (id !== null) {
        this.fetchAllOpenPricingRequests();
      }
    }
  );

  // Pricing requests
  @observable public loadingPricingRequests: boolean = false;
  @observable public pricingRequests: IPricingRequest[] = [];
  @computed public get pricingRequestsLength(): number {
    return this.pricingRequests.length;
  }

  // Fetch all open pricing requests
  @action public fetchAllOpenPricingRequests = async (): Promise<
    IPricingRequest[]
  > => {
    try {
      this.loadingPricingRequests = true;

      const pricingRequestApi = await app.service('pricing-request');
      const pricingRequestFind: IGetPricingRequestApi = await pricingRequestApi.find(
        {
          query: {
            $limit: 999999,
            $skip: 0,
            complete: 0,
            $sort: {
              createdAt: 1,
            },
          },
        }
      );

      this.pricingRequests = pricingRequestFind.data;

      return pricingRequestFind.data;
    } catch (err) {
      console.error(err);
      return err;
    } finally {
      this.loadingPricingRequests = false;
    }
  };

  // Single pricing request
  @observable public loadingSinglePricingRequest: boolean = false;
  @observable
  public statusOfSinglePricingRequestApproval: StatusOfNewPricingRequest =
    StatusOfNewPricingRequest.None;
  @observable
  public singlePricingRequest: IPricingRequestDetails | null = null;

  // Fetch details of single pricing request
  @action public fetchSinglePricingRequest = async (
    requestId: number
  ): Promise<any> => {
    try {
      this.loadingSinglePricingRequest = true;

      const pricingRequestApi = await app.service('pricing-request');
      const pricingRequest: IPricingRequestDetails = await pricingRequestApi.get(
        requestId
      );

      this.singlePricingRequest = {
        ...pricingRequest,
        groupItems: [],
        providerGroup: null,
        approvedPricesModified: false,
        groupPricesModified: false,
      };

      return pricingRequest;
    } catch (err) {
      console.error(err);
      return err;
    } finally {
      this.loadingSinglePricingRequest = false;
    }
  };

  // Change approved price of single loaded pricing request
  @action public changeApprovedPriceOfSinglePricingRequest = (
    itemId: number,
    price: string
  ) => {
    const itemsIds =
      this.singlePricingRequest !== null &&
      this.singlePricingRequest.item !== null
        ? this.singlePricingRequest.item.map((i) => i.id)
        : [];

    const itemIndex = itemsIds.indexOf(itemId);
    if (itemIndex !== -1) {
      this.singlePricingRequest.item[itemIndex].approvedPrice = price;
    }

    if (this.singlePricingRequest) {
      this.singlePricingRequest.approvedPricesModified = true;
    }
  };

  // Reset details of single pricing request
  @action public resetSinglePricingRequest = async (): Promise<boolean> => {
    try {
      this.loadingSinglePricingRequest = false;
      this.singlePricingRequest = null;

      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  // Save changes to approve prices of items in single request
  @action
  public saveSinglePricingRequestItemApprovedPricesChanges = async (): Promise<
    boolean
  > => {
    try {
      this.statusOfSinglePricingRequestApproval =
        StatusOfNewPricingRequest.Saving;

      // Updated approved price of all items
      const pricingRequestItemService = await app.service(
        'pricing-request-line'
      );
      const allApprovedPricesUpdated = await this.singlePricingRequest.item.map(
        async (item, i) => {
          const itemApprovedPrice = unformat(item.approvedPrice);
          if (
            item.approvedPrice.trim() !== '' &&
            typeof itemApprovedPrice === 'number'
          ) {
            await pricingRequestItemService.patch(item.id, {
              approvedPrice: itemApprovedPrice,
            });
          }
        }
      );

      // Show success message
      this.statusOfSinglePricingRequestApproval =
        StatusOfNewPricingRequest.Success;

      // Close modal after short delay
      setTimeout(() => {
        this.resetSinglePricingRequest();
      }, 512);

      return true;
    } catch (err) {
      this.statusOfSinglePricingRequestApproval =
        StatusOfNewPricingRequest.ErrorTryAgain;

      console.error(err);
      return err;
    }
  };

  // Approve pricing request
  @action public approveSinglePricingRequest = async (): Promise<boolean> => {
    try {
      this.statusOfSinglePricingRequestApproval =
        StatusOfNewPricingRequest.Saving;

      // Update approved price of all items first
      const pricingRequestItemService = await app.service(
        'pricing-request-line'
      );
      const allApprovedPricesUpdated = await this.singlePricingRequest.item.map(
        async (item, i) => {
          const itemApprovedPrice = unformat(item.approvedPrice);
          if (
            item.approvedPrice.trim() !== '' &&
            typeof itemApprovedPrice === 'number'
          ) {
            await pricingRequestItemService.patch(item.id, {
              approvedPrice: itemApprovedPrice,
            });
          }
        }
      );

      const pricingRequestService = await app.service('pricing-request');
      const approvedPricingRequest = await pricingRequestService.patch(
        this.singlePricingRequest.id,
        {
          complete: true,
        }
      );

      // Show success message
      this.statusOfSinglePricingRequestApproval =
        StatusOfNewPricingRequest.Success;

      // Refresh pricing requests
      this.fetchAllOpenPricingRequests();

      // Close modal after short delay
      setTimeout(() => {
        this.resetSinglePricingRequest();
      }, 512);

      return true;
    } catch (err) {
      if (
        this.statusOfSinglePricingRequestApproval ===
        StatusOfNewPricingRequest.Saving
      ) {
        this.statusOfSinglePricingRequestApproval =
          StatusOfNewPricingRequest.ErrorTryAgain;
      }

      console.error(err);
      return err;
    }
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/stores/PricingRequest.ts